import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { industryMap } from "../../utils/industryMap";

export default function SearchBar({ onSearch, initialSearchQuery }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialSearchQuery || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isInitialMount = useRef(true);

  const handleSearch = useCallback(
    async (searchQuery) => {
      const cleanedQuery = searchQuery.trim();
      if (!cleanedQuery) return;

      try {
        // Update URL first
        navigate(`/lookup/players/${encodeURIComponent(cleanedQuery)}`);
        setLoading(true);
        setError(null);

        const tokenRes = await axios.get("https://api.pixelore.wiki/auth/get-auth-token");
        const token = tokenRes.data.token;

        // Step 3: Build query parameters
        const params = { detail: 5, quests: true };
        const uidRegex = /^[0-9a-fA-F]{24}$/;

        if (cleanedQuery.startsWith("0x") || cleanedQuery.startsWith("ronin:")) {
          params.cryptoWalletAddress = cleanedQuery;
        } else if (uidRegex.test(cleanedQuery)) {
          params.mid = cleanedQuery;
        } else {
          params.username = cleanedQuery;
        }

        const response = await axios.get("https://api.pixelore.wiki/api/playerEndpoint", {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const player = response.data.player;
        const getFilteredMemberships = () => {
          if (!player || !player.memberships) return {};

          const filteredMemberships = {};

          // Get counts for guild memberships
          Object.entries(player.memberships).forEach(([membershipId, membershipData]) => {
            if (membershipId.startsWith("guild-")) {
              const name = membershipId.split("-")[2]; // Extract the name part (e.g., "p2g")
              filteredMemberships[name] = membershipData.count || 0;
            }
          });

          return filteredMemberships;
        };

        const mappedPlayerData = {
          _id: player._id,
          name: player.username,
          skills: Object.entries(industryMap)
            .filter(([id, industry]) => industry.image && !id.endsWith("_recipes"))
            .map(([id, industry]) => {
              const apiSkill = player.levels[id];
              return {
                name: industry.name,
                icon: industry.image,
                level: apiSkill?.level || 1,
                exp: apiSkill?.exp || 0,
              };
            }),
          createdAt: player.createdAt,
          vip: player.memberships?.vip || null, // ✅ Extract VIP from memberships
          quests: player.quests,
          currentAvatar: {
            ...player.currentAvatar,
            id: player.currentAvatar?.id || null,
            pieces: {
              ...player.currentAvatar?.pieces,
              image: player.currentAvatar?.pieces?.image
                ? player.currentAvatar.pieces.image.startsWith("ipfs://")
                  ? player.currentAvatar.pieces.image.replace("ipfs://", "https://gateway.pinata.cloud/ipfs/")
                  : player.currentAvatar.pieces.image
                : "https://pixelore.s3.eu-central-003.backblazeb2.com/Random+Images/wizard.gif",
            },
          },
          levels: player.levels,
          wallets: {
            cryptoWalletAddresses:
              player.cryptoWallets?.map((wallet) => ({
                address: wallet.address,
                wallet: wallet.wallet,
              })) || [],
            memberships: player.memberships,
            coinInventory: player.coinInventory,
          },
          guildEmblem: player.guildEmblem || "",
          guildHandle: player.guildHandle || "",
          guildRole: player.guildRole || "",
          current: player.current || null,
          faction: player.faction,
          guildMemberships: getFilteredMemberships(),
          listings: response.data.listings,
          lands: response.data.lands,
          pets: response.data.pets,
        };

        onSearch(mappedPlayerData);
      } catch (err) {
        console.error("Error fetching player data:", err);
        setError("Player not found. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [onSearch, navigate],
  );

  const updateUrl = useCallback(
    (playerData) => {
      const searchTerm = playerData?.name || query;
      navigate(`/lookup/players/${encodeURIComponent(searchTerm)}`);
    },
    [query, navigate],
  );

  // Form submission handler
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        const playerData = await handleSearch(query);
        updateUrl(playerData);
      } catch {
        // Error already handled in handleSearch
      }
    },
    [handleSearch, query, updateUrl],
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (initialSearchQuery) {
        setQuery(initialSearchQuery);
        handleSearch(initialSearchQuery).then(updateUrl);
      }
    }
  }, [initialSearchQuery, handleSearch, updateUrl]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col items-center w-full max-w-xl">
      {/* Search Bar */}
      <div className="flex items-center bg-white border border-rose-300 rounded-full shadow-md p-2 w-full">
        <input
          type="text"
          placeholder="Search by Username, Wallet, or UserID..."
          className="flex-1 px-4 py-2 bg-transparent outline-none text-rose-700 placeholder-rose-400"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button type="submit" className="bg-rose-500 hover:bg-rose-600 text-white p-2 rounded-full transition-all" disabled={loading}>
          {loading ? "..." : <Search size={20} />}
        </button>
      </div>

      {/* Loading & Error Messages */}
      {loading && <p className="text-rose-500 mt-2 text-sm">Searching...</p>}
      {error && <p className="text-[var(--red)] mt-2 text-sm">{error}</p>}
    </form>
  );
}
